import * as React from 'react';
import {
  type UniqueIdentifier,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {arrayMove} from '@dnd-kit/sortable';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {createSelector} from 'reselect';
import {Component, Plugins} from '@packages/systems/wfdl';
import {globalComponentsSettingsSaveRequested} from '@packages/systems/global-component-settings';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {selectGlobalComponentIds} from '../flux';
import {groupComponentItems} from '../utils/groupComponentItems';
import type {ComponentItem, ComponentItemMap} from '../types';

const sitePluginName = Plugins.name('__SitePlugin');

/**
 * Returns a record of component IDs to `ComponentItem`s from all user components
 * in the component map's `__SitePlugin` namespace.
 */
const selectAllUserComponents = createSelector(
  (state: DesignerStores) => state.DesignerStore.components,
  (componentMap): ComponentItemMap => {
    return componentMap.reduceAtPluginName(
      sitePluginName,
      (acc, component, componentName) => {
        if (componentName === 'page') {
          return acc;
        }

        acc[componentName] = {
          id: componentName,
          name: Component.getDisplayName(component),
          group: component.groupName || undefined,
          hiddenForNonComponentManagers:
            component.hiddenForNonComponentManagers,
        };

        return acc;
      },
      {} as ComponentItemMap
    );
  }
);

export type GlobalComponentsManagerState = {
  selectedIds: string[];
  selectedComponents: ComponentItem[];
  allComponents: ComponentItemMap;
  availableComponents: ComponentItem[];
  groupedAvailableComponents: Map<string, ComponentItem[]>;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  activeId: UniqueIdentifier | null;
  activeComponent: ComponentItem | null;
  collapsedGroups: Set<string>;
  handleToggleGroup: (groupName: string) => void;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: () => void;
  handleAdd: (componentId: string) => void;
  handleRemove: (componentId: string) => void;
};

/**
 * Hook that manages the editing state for global component settings.
 * Changes are auto-saved whenever the selected component list is modified.
 */
export function useGlobalComponentsManager(): GlobalComponentsManagerState {
  const dispatch = useDispatch();
  const globalComponentIds = useDesignerSelector(selectGlobalComponentIds);
  const allComponents = useDesignerSelector(selectAllUserComponents);
  const canManageComponents = useDesignerSelector(
    selectDesignerAbility('canManageComponents')
  );

  const [selectedIds, setSelectedIds] =
    React.useState<string[]>(globalComponentIds);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);
  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(
    new Set()
  );

  React.useEffect(() => {
    setSelectedIds(globalComponentIds);
  }, [globalComponentIds]);

  React.useEffect(() => {
    const hasChanges =
      selectedIds.length !== globalComponentIds.length ||
      selectedIds.some((id, index) => id !== globalComponentIds[index]);

    if (hasChanges) {
      dispatch(
        globalComponentsSettingsSaveRequested({
          components: selectedIds.map((id) => ({id})),
        })
      );
    }
  }, [selectedIds, globalComponentIds, dispatch]);

  const handleToggleGroup = React.useCallback((groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  }, []);

  const visibleComponents = React.useMemo(() => {
    if (canManageComponents) return allComponents;
    const filtered: ComponentItemMap = {};
    for (const [id, c] of Object.entries(allComponents)) {
      if (!c.hiddenForNonComponentManagers) {
        filtered[id] = c;
      }
    }
    return filtered;
  }, [allComponents, canManageComponents]);

  const availableComponents = React.useMemo(() => {
    return Object.values(visibleComponents).filter(
      (c) =>
        !selectedIds.includes(c.id) &&
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [visibleComponents, selectedIds, searchQuery]);

  const groupedAvailableComponents = React.useMemo(() => {
    return groupComponentItems(availableComponents);
  }, [availableComponents]);

  const selectedComponents = React.useMemo(() => {
    return selectedIds
      .map((id) => visibleComponents[id])
      .filter((c): c is ComponentItem => c !== undefined);
  }, [selectedIds, visibleComponents]);

  const activeComponent = React.useMemo(() => {
    if (!activeId) return null;
    return allComponents[activeId as string] ?? null;
  }, [activeId, allComponents]);

  const handleDragStart = React.useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = React.useCallback((event: DragEndEvent) => {
    const {active, over} = event;

    if (over?.id && active.id !== over.id) {
      setSelectedIds((prevIds) => {
        const oldIndex = prevIds.indexOf(active.id as string);
        const newIndex = prevIds.indexOf(over.id as string);
        return arrayMove(prevIds, oldIndex, newIndex);
      });
    }
    setActiveId(null);
  }, []);

  const handleDragCancel = React.useCallback(() => {
    setActiveId(null);
  }, []);

  const handleAdd = React.useCallback(
    (componentId: string) => {
      if (selectedIds.includes(componentId)) return;
      setSelectedIds((prev) => [...prev, componentId]);
    },
    [selectedIds]
  );

  const handleRemove = React.useCallback((componentId: string) => {
    setSelectedIds((prev) => prev.filter((id) => id !== componentId));
  }, []);

  return {
    selectedIds,
    selectedComponents,
    allComponents,
    availableComponents,
    groupedAvailableComponents,
    searchQuery,
    setSearchQuery,
    activeId,
    activeComponent,
    collapsedGroups,
    handleToggleGroup,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    handleAdd,
    handleRemove,
  };
}
